/**
  * @class mmodMap
  * @description
  * Map module displays map for user with selected features on it.
  * Map module encapsulates mele-map web element and feed it with relevant feature data.
  * Map module listens to multiple messages to get data as follows:
  * - mmGps: to display own position and heading
  * - mmApplications and mmAppGps: to display other unit’s position
  * - mmObjects: to display instructions and incidents
  * Map module loads geography data on connection to server (if configured geodata config item)
  * and feed them to msysGeo library through loadGeoData function.
  * However this can also be compiled into the application directly for demonstration purposes,
  * which often happens in application start script e.g. in mOffice like this:
  *   require("msysGeo").loadGeoData(require("raw-loader!../../../res/geo/geo-slovakia-sample.txt"))
  * Map module also handles user double clicks on map features
  * and translate them into showing particular feature in other relevant module.
  */

import mmodModule from 'mmodModule'
const mfs = require('msysFS')
const geo = require('msysGeo')
const MAX_SEARCH_RESULTS = 30

export default class mmodMap extends mmodModule {
  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Creates an instance of the class.
  * @param {string} [id] - ID under which the object will listen for messages
  * @param {string} [cfg] - object initial configuration
  * @param {string} [cfg_schema] - additional schema properties from derived classes
  * @param {string} [dependencies] - additional dependencies from derived classes
  */
  constructor(id, cfg, cfg_schema, dependencies) {
    dependencies = _.union([/* put module specific dependencies here */], dependencies)
    cfg_schema = _.extend({
      init: {
        type: 'object',
        default: { type: 'full' },
        description: 'Initial window setup; same structure as required by `mmWindowSet`',
      },
      center: { type: 'string', description: 'Initial map center as geohash', default: 'u2s1yh4dzj87' },
      geodata: { type: 'string', description: 'uri to msysProxy file', default: '' },
      base: { type: 'string', description: 'base layer for map; if not specified, will be automatic according to theme' },
      layerlist: { type: 'boolean', description: 'if true, will show layerlist on map', default: true },
    }, cfg_schema)
    super(id, cfg, cfg_schema, dependencies)

    this.applications = {}
    this.wf = {}
    this.applications_gps = {}
  } // constructor

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * initialization of GUI components
  */
  initWindow() {
    this.loadForm(require('./mmodMap.json'))
    this._map = this.el.find('mele-map')
    if (this.config.base) this._map.base = this.config.base
    this._map.eventDoubleClicked = this.doubleClicked.bind(this)
    this._map.center = this.config.center
    this._map.selected = ['gps']
    if (this.config.layerlist) {
      this._map.layerlist = mfs.device() !== 'phone'
    }
    this.mmStart()
    this.subscribe('#GPS')
    this.subscribe('#MAP')
    this.subscribe('#APPS')
    this.subscribe('#WFINSTRUCTIONS')
    this.subscribe('#WFINCIDENTS')
    this.subscribe('#WFACCIDENTS')
    // this.loadGeoData(true);
  } // initWindow

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * try to load geo data file
  */
  async loadGeoData(first) {
    const uri = this.config.geodata
    if (!uri) return
    try {
      const { params } = await this.request('msysProxy', 'mmFileGet', { uri }, 'mmFile')
      const data = mfs.base64ToUTF8(params.data)
      geo.loadGeoData(data)
      this.geodataLoaded = 1
    } catch (e) {
      this.warn('Cannot load geo data from ' + uri)
      this.geodataLoaded = first ? 0 : -1
    }
  } // loadGeoData

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * information on change of location
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} [params.dev] - ID of device producing the GPS info
  * @param {string} params.gps - geohash of GPS position
  * @param {number} [params.speed] - speed in km/h
  * @param {number} [params.heading] - movement heading in degrees
  * @param {integer} [params.satellites] - number of satellites
  * @param {integer} [params.gps_dt] - GPS reported time in microseconds
  * @param {integer} [params.fix] - true if exists on sattelite
  */
  mmGps(sender, params) {
    if (!this._map) return
    this._map.gps = params.gps + '/' + params.heading
  } // mmGps

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * double click on unit will open mmodUnits, mmodInstructions, mmodAccidents, mmodIncidents with this selected
  * @param {object} el - mele-map element
  * @param {object} key - identifier of object on which user double clicked
  */
  doubleClicked(el, key) {
    let marker
    [marker, key] = key.split(':')
    const target = 'mmod' + marker.replace(/\w/, c => c.toUpperCase()) + 's'
    if (['instruction', 'incident', 'accident', 'unit'].includes(marker) && key) {
      this.publish(target, 'mmListSelected', { holder: 'list', selected: key })
      this.publish('msysWM', 'mmWindowFocus', { id: target })
    }
  }

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * information from server on location change of other application
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {array} params.list - array of changed records
  */
  mmAppGps(sender, params) {
    params.list.forEach(rec => {
      this.applications_gps[rec.id] = rec
      this.refreshUnit(rec.id)
    })
  } // mmAppGps

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * server sent list of applications
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {boolean} [params.full] - whether it is full or incremental update
  * @param {integer} [params.limit] - how many items are sent in list
  * @param {integer} [params.total] - total number of items on server
  * @param {integer} [params.version] - version of data sent by server
  * @param {array} [params.list] - array of changed records
  */
  mmApplications(sender, params) {
    if (params.full) {
      this.applications = {}
      this._map.deleteItems('unit:')
    }
    const changes = params.list || []
    changes.forEach(change => {
      this.applications[change.id] = change
      this.refreshUnit(change.id)
    })
  } // mmApplications

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * refresh application on map
  * @param {string} app_id - application ID to be refreshed
  */
  refreshUnit(app_id) {
    app_id = _.toString(app_id)
    const app = this.applications[app_id]
    const gps = this.applications_gps[app_id]
    if (!app_id || !app || !gps || !app.user_id || !app.callsign || app.status == 'bout') {
      // delete app from map
      this._map.deleteItem('unit:' + app_id)
    } else {
      // draw app on map
      const colors = { g: 'green', o: 'orange', r: 'red', b: 'black' }
      const status = _.toString(app.status)
      const callsign = _.toString(app.callsign)
      const color = colors[status.substr(0, 1)] || 'black'
      this._map.setItem({
        marker: 'unit',
        key: 'unit:' + app_id,
        shape: 'D,' + gps.gps,
        label: callsign,
        label2: 'user.fullname.' + _.toString(app.user_id),
        icon: 'user.icon.' + _.toString(app.user_id),
        heading: gps.heading,
        color,
        actions: ['message', 'details'],
        data: { status, callsign },
      }, !this.el.focused)
    }
  } // refreshUnit

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * request to select object on map
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} [params.type] - type of object to center
  * @param {string} [params.id] - id of the object
  * @param {number} [params.zoom] - desired zoom
  */
  mmMapSelect(sender, params) {
    this._map.selected = [params.type + ':' + params.id]
    const zoom = parseInt(params.zoom)
    if (zoom > 0) this._map.zoom = zoom
  } // mmMapSelect

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Sends by msvcSkyPhone when HW button is pressed
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} params.key - key attribute of the button
  */
  mmHWButton(sender, params) {
    if (!this.el.focused) {
      return
    }

    if (params.key == 'volumeup' && this._map.zoom < this._map.maxzoom) {
      this._map.zoom++
    }

    if (params.key == 'volumedown' && this._map.zoom > this._map.minzoom) {
      this._map.zoom--
    }
  } // mmHWButton

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * msysDomainClient informs all listeners that connection to mServer is established
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} [params.app_id] - app_id of the server
  * @param {string} params.url - URL of connected server
  * @param {string} [params.ident] - Identity string of server
  * @param {string} [params.client_ip] - IP of the client as recognized by the server
  */
  mmServerConnected(sender, params) {
    if (!this.geodataLoaded) {
      this.loadGeoData(false)
    }
    this._map.redraw()
  } // mmServerConnected

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Sends by Window Manager when top full-screen window on any screen changes. Sends directly to that window and to #WM queue.
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} [params.id]
  * @param {integer} [params.screen]
  */
  mmWindowFocused(sender, params) {
    // if(!params.id){
    //   this._map.redraw();
    // }
  } // mmWindowFocused

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * used to return list of workflow processes
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {boolean} [params.full] - whether it is full or incremental update
  * @param {boolean} [params.limited] - indicates if data were limited to some fields only
  * @param {integer} [params.version] - version of data sent by server
  * @param {array} [params.sort] - sort criteria
  * @param {object} [params.query] - query parameters
  * @param {string} [params.type] - type of returned objects, e.g. id of workflow producing the objects
  * @param {integer} [params.total] - total number of objects matching the query
  * @param {integer} [params.offset] - offset of returned objects
  * @param {integer} [params.limit] - limits used in the query
  * @param {array} params.list - array of matching objects
  */
  mmObjects(sender, params) {
    const updateFcn = this['update' + _.capitalize(params.type)]
    if (typeof (updateFcn) === 'function') {
      updateFcn.call(this, params.full, params.list)
    }
  } // mmObjects

  updateInstructions(full, list) {
    const singular = 'instruction'
    const plural = singular + 's'
    if (full) this._map.deleteItems(singular + ':')
    list.forEach(change => {
      const location = _.toString(change.location).match(/^((\w+)(?:\.\w*)?)(?:;([\w\,]*))?(?:\/(.*))?$/)
      if (!location) return
      const [, geoid, center, shape, legend] = location
      if (change.assignee == this.user_id) {
        // display only instructions assigned to me
        this._map.setItem({
          key: singular + ':' + change.id,
          marker: singular,
          center,
          shape,
          label: `wf.${plural}.type.${change.type}`,
          label2: `datetime:${change.start_dt},${change.end_dt}`,
          description: change.description,
          value: `wf.${plural}.state.${change.state}`,
          color: change.state == 'ASSIGNED' ? 'blue' : 'lightblue',
          actions: ['details'],
        })
      } else {
        this._map.deleteItem(singular + ':' + change.id)
      }
    })
  } // updateInstructions

  updateIncidents(full, list) {
    const singular = 'incident'
    const plural = singular + 's'
    if (full) this._map.deleteItems(singular + ':')
    list.forEach(change => {
      const location = _.toString(change.location).match(/^((\w+)(?:\.\w*)?)(?:;([\w\,]*))?(?:\/(.*))?$/)
      if (!location) return
      const [, geoid, center, shape, legend] = location
      if (change.assignee == this.user_id) {
        // display only instructions assigned to me
        this._map.setItem({
          key: singular + ':' + change.id,
          marker: singular,
          center,
          shape,
          label: `wf.${plural}.category.${change.category}`,
          label2: `datetime:${change.created_dt}`,
          value: `{{wf.${plural}.assignee}}: {{user.fullname.${change.assignee}}}<br/>{{wf.${plural}.state}}: {{wf.${plural}.state.${change.state}}}`,
          description: change.description,
          color: 'red',
          actions: ['details'],
        })
      } else {
        this._map.deleteItem(singular + ':' + change.id)
      }
    })
  } // updateIncidents

  updateEnergybuildings(full, list) {
    const singular = 'building'
    const plural = singular + 's'
    if (full) this._map.deleteItems(singular + ':')
    list.forEach(change => {
      const location = _.toString(change.gps).match(/^((\w+)(?:\.\w*)?)(?:;([\w\,]*))?(?:\/(.*))?$/)
      if (!location) return
      const [, geoid, center, shape, legend] = location
      this._map.setItem({
        key: singular + ':' + change.id,
        marker: singular,
        center,
        shape,
        label: change.name,
        label2: change.city,
        description: '',
        value: '',
        actions: [],
      })
    })
  } // updateInstructions

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Sends by <mele-marker> when clicked on marker action
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} params.holder - key attribute of the mele-marker
  * @param {string} params.action - action clicked
  */
  mmMapMarkerAction(sender, params) {
    const [type, id] = _.toString(params.holder).split(':')
    if (params.action == 'details') {
      const target =
        type == 'instruction' ? (MOSY_APP == 'mPatrol' ? 'mmodInstructions' : 'mmodOfficeInstructionsLive') :
          type == 'incident' ? (MOSY_APP == 'mPatrol' ? 'mmodIncidents' : 'mmodOfficeIncidentsLive') :
            type == 'accident' ? (MOSY_APP == 'mPatrol' ? 'mmodAccidents' : 'mmodOfficeAccidents') :
              type == 'unit' ? 'mmodUnits' :
                null
      if (target) {
        this.publish(target, 'mmOpen', { id })
        this.publish('msysWM', 'mmWindowFocus', { id: target })
      }
    } else if (params.action == 'message') {
      const user_id = _.get(this.applications[id], 'user_id')
      if (user_id) {
        this.publish('msysWM', 'mmWindowFocus', { id: 'mmodChats' })
        this.publish('mmodChats', 'mmChatNew', { user_id })
        this.publish('msysWM', 'mmWindowFocus', { id: 'mmodChats' })
        this.publish('mmodChats', 'mmChatNew', { user_id })
      }
    }
  } // mmMapMarkerAction
} // mmodMap
